import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from '../../../../../constants/common.constant';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import AppPaperTitle from 'src/app/common/components/AppPaperTitle';
import { PruTableHeader } from 'src/app/common/components/Table/PruTableHeader';

import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import useEventCallback from 'src/app/common/hook/useEventCallback';
import TableHeaderFilterLabel from '../../../../../../../common/components/TableHeaderFilterLabel';
import useStyles from './PolicyTable.styles';
import { useBatchFilesViewPageContext } from '../../BatchFilesView.context';

const EMPTY_FILTER_VALUE = '(Empty)';

const withEmpty = (sorted: string[]): string[] => [EMPTY_FILTER_VALUE, ...sorted];

const defaultColumnFilters: Record<ColumnKey, string[]> = {
  policyNumber: [],
  policyStatusCode: [],
  currentAgentCode: [],
  soldUnit: [],
  importedAgent: [],
  suggestedAgent: [],
  suggestedAgentName: [],
  _selectedQualifiedAgent: [],
  selectedAgent: [],
  selectedAgentName: [],
  reasonSelectedAnotherAgent: [],
  _sentBackFromLabel: [],
  _policyTaskStatusLabel: [],
  wasPutOnHold: [],
};

type ColumnKey =
  | 'policyNumber'
  | 'policyStatusCode'
  | 'currentAgentCode'
  | 'soldUnit'
  | 'importedAgent'
  | 'suggestedAgent'
  | 'suggestedAgentName'
  | '_selectedQualifiedAgent'
  | 'selectedAgent'
  | 'selectedAgentName'
  | 'reasonSelectedAnotherAgent'
  | '_sentBackFromLabel'
  | '_policyTaskStatusLabel'
  | 'wasPutOnHold';

const PolicyTable = () => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const { classes } = useStyles();
  const { policies } = useBatchFilesViewPageContext();

  const [columnFilters, setColumnFilters] = useState<Record<ColumnKey, string[]>>(defaultColumnFilters);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE_OPTIONS[0]);
  const [filteredItems, setFilteredItems] = useState([...policies]);

  const columnOptions = useMemo<Record<ColumnKey, string[]>>(
    () => ({
      policyNumber: withEmpty(
        [...new Set(policies.map((i) => String(i.policyNumber ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      policyStatusCode: withEmpty(
        [...new Set(policies.map((i) => String(i.policyStatusCode ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      currentAgentCode: withEmpty(
        [...new Set(policies.map((i) => String(i.currentAgentCode ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      soldUnit: withEmpty([...new Set(policies.map((i) => String(i.soldUnit ?? '')) ?? [])].filter(Boolean).sort()),
      importedAgent: withEmpty(
        [...new Set(policies.map((i) => String(i.importedAgent ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      suggestedAgent: withEmpty(
        [...new Set(policies.map((i) => String(i.suggestedAgent ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      suggestedAgentName: withEmpty(
        [...new Set(policies.map((i) => String(i.suggestedAgentName ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      _selectedQualifiedAgent: withEmpty(
        [...new Set(policies.map((i) => String(i._selectedQualifiedAgent ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      selectedAgent: withEmpty(
        [...new Set(policies.map((i) => String(i.selectedAgent ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      selectedAgentName: withEmpty(
        [...new Set(policies.map((i) => String(i.selectedAgentName ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      reasonSelectedAnotherAgent: withEmpty(
        [...new Set(policies.map((i) => String(i.reasonSelectedAnotherAgent ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      _sentBackFromLabel: withEmpty(
        [...new Set(policies.map((i) => String(i._sentBackFromLabel ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      _policyTaskStatusLabel: withEmpty(
        [...new Set(policies.map((i) => String(i._policyTaskStatusLabel ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      wasPutOnHold: withEmpty(
        [...new Set(policies.map((i) => (i.wasPutOnHold ? '✓' : '✕')) ?? [])].filter(Boolean).sort(),
      ),
    }),
    [policies],
  );

  const changeFilteredItems = useEventCallback((payload?: { columnFilters: typeof columnFilters }) => {
    const { columnFilters } = payload || {};

    const matchStr = (filter: string[] | null | undefined, val: string | null | undefined): boolean => {
      if (['null', 'undefined'].includes(typeof filter) || !filter?.length) return true;
      const s = String(val ?? '');
      return filter!.some((f) => (f === EMPTY_FILTER_VALUE ? !s : f === s));
    };
    setFilteredItems(
      policies?.filter(
        (item) =>
          matchStr(columnFilters?.policyNumber, item.policyNumber) &&
          matchStr(columnFilters?.policyStatusCode, item.policyStatusCode) &&
          matchStr(columnFilters?.currentAgentCode, item.currentAgentCode) &&
          matchStr(columnFilters?.soldUnit, item.soldUnit) &&
          matchStr(columnFilters?.importedAgent, item.importedAgent) &&
          matchStr(columnFilters?.suggestedAgent, item.suggestedAgent) &&
          matchStr(columnFilters?.suggestedAgentName, item.suggestedAgentName) &&
          matchStr(columnFilters?._selectedQualifiedAgent, item._selectedQualifiedAgent) &&
          matchStr(columnFilters?.selectedAgent, item.selectedAgent) &&
          matchStr(columnFilters?.selectedAgentName, item.selectedAgentName) &&
          matchStr(columnFilters?.reasonSelectedAnotherAgent, item.reasonSelectedAnotherAgent) &&
          matchStr(columnFilters?._sentBackFromLabel, item._sentBackFromLabel) &&
          matchStr(columnFilters?._policyTaskStatusLabel, item._policyTaskStatusLabel) &&
          matchStr(columnFilters?.wasPutOnHold, item.wasPutOnHold ? '✓' : '✕'),
      ),
    );
  });

  const makeFilterHandler = (col: ColumnKey) => (selected: string[]) => {
    setPage(0);
    const validSelected = selected.filter((s) => columnOptions[col].includes(s));
    const newColumnFilters = {
      ...columnFilters,
      [col]: validSelected.length === 0 || validSelected.length === columnOptions[col].length ? [] : validSelected,
    };
    setColumnFilters(newColumnFilters);
    changeFilteredItems({ columnFilters: newColumnFilters });
  };

  const paginatedItems = useMemo(
    () => filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredItems, page, rowsPerPage],
  );

  const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilters = () => {
    setColumnFilters(defaultColumnFilters);
    changeFilteredItems({ columnFilters: defaultColumnFilters });
    setPage(0);
  };

  return (
    <Paper elevation={0} variant="elevation" className={classes.root}>
      <AppPaperTitle>
        {Translation('opm_pd.policyList')}
        <Box display="flex" flex={1} alignItems="center" justifyContent="flex-end" gap={1}>
          <Button
            variant="outlined"
            disabled={Object.values(columnFilters).every((v) => v.length === 0)}
            onClick={handleClearFilters}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {Translation('opm_pd.clearFilters')}
          </Button>
        </Box>
      </AppPaperTitle>
      <div>
        <TableContainer sx={{ maxHeight: `calc(100dvh - 220px)` }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.policyNumber')}
                    options={columnOptions.policyNumber}
                    selectedOptions={columnFilters.policyNumber ?? []}
                    onSelectionChange={makeFilterHandler('policyNumber')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.policyStatusCode')}
                    options={columnOptions.policyStatusCode}
                    selectedOptions={columnFilters.policyStatusCode ?? []}
                    onSelectionChange={makeFilterHandler('policyStatusCode')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.currentAgentCode')}
                    options={columnOptions.currentAgentCode}
                    selectedOptions={columnFilters.currentAgentCode ?? []}
                    onSelectionChange={makeFilterHandler('currentAgentCode')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.soldUnit')}
                    options={columnOptions.soldUnit}
                    selectedOptions={columnFilters.soldUnit ?? []}
                    onSelectionChange={makeFilterHandler('soldUnit')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.importedAgent')}
                    options={columnOptions.importedAgent}
                    selectedOptions={columnFilters.importedAgent ?? []}
                    onSelectionChange={makeFilterHandler('importedAgent')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.suggestedAgent')}
                    options={columnOptions.suggestedAgent}
                    selectedOptions={columnFilters.suggestedAgent ?? []}
                    onSelectionChange={makeFilterHandler('suggestedAgent')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.suggestedAgentName')}
                    options={columnOptions.suggestedAgentName}
                    selectedOptions={columnFilters.suggestedAgentName ?? []}
                    onSelectionChange={makeFilterHandler('suggestedAgentName')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.otherQualifiedAgent')}
                    options={columnOptions._selectedQualifiedAgent}
                    selectedOptions={columnFilters._selectedQualifiedAgent ?? []}
                    onSelectionChange={makeFilterHandler('_selectedQualifiedAgent')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.selectedAgent')}
                    options={columnOptions.selectedAgent}
                    selectedOptions={columnFilters.selectedAgent ?? []}
                    onSelectionChange={makeFilterHandler('selectedAgent')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.selectedAgentName')}
                    options={columnOptions.selectedAgentName}
                    selectedOptions={columnFilters.selectedAgentName ?? []}
                    onSelectionChange={makeFilterHandler('selectedAgentName')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.reasonForSelectingAnotherAgent')}
                    options={columnOptions.reasonSelectedAnotherAgent}
                    selectedOptions={columnFilters.reasonSelectedAnotherAgent ?? []}
                    onSelectionChange={makeFilterHandler('reasonSelectedAnotherAgent')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.reasonRejectSendBack')}
                    options={columnOptions._sentBackFromLabel}
                    selectedOptions={columnFilters._sentBackFromLabel ?? []}
                    onSelectionChange={makeFilterHandler('_sentBackFromLabel')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.status')}
                    options={columnOptions._policyTaskStatusLabel}
                    selectedOptions={columnFilters._policyTaskStatusLabel ?? []}
                    onSelectionChange={makeFilterHandler('_policyTaskStatusLabel')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.wasPutOnHold')}
                    options={columnOptions.wasPutOnHold}
                    selectedOptions={columnFilters.wasPutOnHold ?? []}
                    onSelectionChange={makeFilterHandler('wasPutOnHold')}
                  />
                </PruTableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedItems.map((item, index) => (
                <TableRow key={item.policyNumber ?? index} hover>
                  <TableCell>{item.policyNumber ?? '-'}</TableCell>
                  <TableCell>{item.policyStatusCode ?? '-'}</TableCell>
                  <TableCell>{item.currentAgentCode ?? '-'}</TableCell>
                  <TableCell>{item.soldUnit ?? '-'}</TableCell>
                  <TableCell>{item.importedAgent ?? '-'}</TableCell>
                  <TableCell>{item.suggestedAgent ?? '-'}</TableCell>
                  <TableCell>{item.suggestedAgentName ?? '-'}</TableCell>
                  <TableCell>{item._selectedQualifiedAgent ?? '-'}</TableCell>
                  <TableCell>{item?.selectedAgent ?? '-'}</TableCell>
                  <TableCell>{item?.selectedAgentName ?? '-'}</TableCell>
                  <TableCell>{item?.reasonSelectedAnotherAgent ?? '-'}</TableCell>
                  <TableCell>{item?._sentBackFromLabel ?? '-'}</TableCell>
                  <TableCell>{item?._policyTaskStatusLabel ?? '-'}</TableCell>
                  <TableCell>{item?.wasPutOnHold ? '✓' : '✕'}</TableCell>
                </TableRow>
              ))}
              {paginatedItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={18} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {Translation('opm_pd.noData')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredItems.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </div>
    </Paper>
  );
};

export default PolicyTable;
